import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculatorIcon from '@mui/icons-material/Calculate';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiClientRaw } from '../../globals/utils/api';
import PremiumCalculator from '../SHARED/PremiumCalculatorPublic';
import SupportContactDialog from '../../components/SupportContactDialog';
import useDuplicateEmailCheck from '../SHARED/Utilities/useDuplicateEmailCheck';
import DuplicateEmailAlert from '../SHARED/Utilities/DuplicateEmailAlert';
import { REGISTRATION_PAGE_UI_TEAM_MEMBER } from '../SHARED/Utilities/registrationPageUi';

const TeamMemberRegister = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    username: '',
    phonenumber: '',
    password: '',
    confirmPassword: '',
  });

  // Error states for inline validation
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [phonenumberError, setPhonenumberError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const duplicateEmailCheck = useDuplicateEmailCheck();

  const primaryColor = '#333333';
  const secondaryColor = '#666666';

  const validateName = () => {
    if (!formData.name.trim()) {
      setNameError('Full name is required');
      return false;
    }
    setNameError('');
    return true;
  };

  const validatePhonenumber = () => {
    if (!formData.phonenumber.trim()) {
      setPhonenumberError('Phone number is required');
      return false;
    }
    setPhonenumberError('');
    return true;
  };

  const validatePassword = () => {
    if (!formData.password.trim()) {
      setPasswordError('Password is required');
      return false;
    }
    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setPasswordError('Password must contain at least one number');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setPasswordError('Password must contain at least one special character');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = () => {
    if (!formData.confirmPassword.trim()) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    switch (name) {
      case 'username':
        if (usernameError) setUsernameError('');
        break;
      case 'email':
        if (emailError) setEmailError('');
        break;
      case 'name':
        if (nameError) setNameError('');
        break;
      case 'phonenumber':
        if (phonenumberError) setPhonenumberError('');
        break;
      case 'password':
        if (passwordError) setPasswordError('');
        break;
      case 'confirmPassword':
        if (confirmPasswordError) setConfirmPasswordError('');
        break;
      default:
        break;
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    if (!validateUsername() || !validateEmail() || !validateName() || 
        !validatePhonenumber() || !validatePassword() || !validateConfirmPassword()) {
      return;
    }

    setLoading(true);
  
    try {
      const response = await apiClientRaw(`/carrier-team-member/create/${token}`, "POST", {
        user: {
          username: formData.username,
          password: formData.password,
          email: formData.email,
        },
        team_member_phonenumber: formData.phonenumber
      });
  
      if (response && response.status >= 200 && response.status < 300) {
        toast.success("Carrier Team Member registered successfully");
        navigate('/');
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.user) {
          if (errorData.user.username) {
            errorData.user.username.forEach((msg) => toast.error(`Invalid Username: ${msg==='vermost portal user with this username already exists.'?'Username already taken. Please try another one':msg}`));
          }
          if (errorData.user.password) {
            errorData.user.password.forEach((msg) => toast.error(`Invalid Password: ${msg}`));
          }
        }
        if (errorData.fullname) {
          errorData.fullname.forEach((msg) => toast.error(`Full Name: ${msg}`));
        }
      } else {
        toast.error("An error occurred during registration. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') handleRegister(event);
  };

  const { validateEmail, validateUsername, passwordFields } = REGISTRATION_PAGE_UI_TEAM_MEMBER.compose({
    formData, setEmailError, setUsernameError, passwordError, confirmPasswordError, primaryColor, handleInputChange, validatePassword, validateConfirmPassword,
  });

  return (
    <Box
      display="flex"
      flexDirection="row"
      minHeight="100vh"
      sx={{ 
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Left Column - Hero Section */}
      <Box
        sx={{
          width: isMobile ? '0%' : '60%',
          display: isMobile ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%, #000000 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 30%, rgba(51, 51, 51, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
            '@media (max-width: 768px)': {
              background: 'transparent'
            }
          }
        }}
      >
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(51, 51, 51, 0.3), rgba(102, 102, 102, 0.1))',
          filter: 'blur(40px)',
          animation: 'float 6s ease-in-out infinite',
          '@media (max-width: 768px)': {
            display: 'none'
          }
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '25%',
          right: '10%',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(51, 51, 51, 0.2), rgba(102, 102, 102, 0.05))',
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite reverse',
          '@media (max-width: 768px)': {
            display: 'none'
          }
        }} />
        
        {/* Logo */}
        <Box
          component="img"
          src="/LogoNew.png"
          alt="Company Logo"
          sx={{
            position: 'absolute',
            top: 40,
            left: 40,
            width: '120px',
            height: 'auto',
            zIndex: 10,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
            '@media (max-width: 1024px) and (min-width: 768px)': {
              top: 20,
              left: 20,
              width: '60px',
              zIndex: 15
            }
          }}
        />

        {/* Darren Image - Main Focus */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.3) 0%, transparent 50%, rgba(51, 51, 51, 0.1) 100%)',
            zIndex: 1,
            '@media (max-width: 768px)': {
              background: 'transparent'
            }
          }
        }}>
          <Box 
            component="img" 
            src="/DarrenImage.png" 
            alt="Darren Vermost" 
            sx={{ 
              maxHeight: '85vh',
              maxWidth: '90%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 40px rgba(51, 51, 51, 0.2)',
              border: '2px solid rgba(51, 51, 51, 0.3)',
              zIndex: 2,
              filter: 'contrast(1.1) brightness(1.05)',
              '@media (max-width: 768px)': {
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                border: 'none',
                filter: 'none'
              }
            }} 
          />
        </Box>
      </Box>

      {/* Right Column - Registration Form */}
      <Box
        sx={{
          width: isMobile ? '100%' : '40%',
          display: 'flex',
          paddingY: '8vh',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fafafa',
          position: 'relative',
          minHeight: '100vh',
          background: isMobile ? 
            'white' :
            'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
          '&::before': isMobile ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            pointerEvents: 'none'
          } : {}
        }}
      >
        {/* Registration Form Container */}
        <Box
          sx={{
            width: '90%',
            maxWidth: '480px',
            backgroundColor: 'transparent',
            p: { xs: 3, sm: 4 },
            position: 'relative'
          }}
        >
          {/* Company Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box 
              component="img" 
              src="/ProbateBondPros.png" 
              alt="Probate Bond Pros Logo" 
              sx={{ 
                height: 60, 
                maxWidth: '100%',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))'
              }} 
            />
          </Box>

          {/* Form Header */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box sx={{ 
              position: 'absolute', 
              top: 40, 
              left: 0,
              '@media (max-width: 768px)': {
                left: -10
              }
            }}>
              <IconButton 
                onClick={() => navigate(-1)} 
                sx={{ 
                  color: primaryColor,
                  backgroundColor: 'rgba(51, 51, 51, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(51, 51, 51, 0.2)'
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Welcome Section */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: primaryColor, 
              mb: 0.5,
              fontSize: { xs: '1.6rem', sm: '1.8rem' }
            }}>
              Create Team Member Account
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              fontSize: '1rem',
              fontWeight: 400
            }}>
              Please fill out the form to create your account
            </Typography>
          </Box>

          {/* Form Fields */}
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Username
              </Typography>
              <Tooltip 
                title="Enter a unique username for your account"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="username"
              type="text"
              required
              value={formData.username}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={validateUsername}
              placeholder="Enter your username here"
              error={!!usernameError}
              helperText={usernameError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: usernameError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: usernameError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: usernameError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Email Address
              </Typography>
              <Tooltip 
                title="Enter your email address"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              onBlur={() => {
                if (validateEmail()) duplicateEmailCheck.checkEmail(formData.email);
              }}
              placeholder="Enter your email address here"
              error={!!emailError}
              helperText={emailError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: emailError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: emailError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: emailError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
            />
            <DuplicateEmailAlert
              accounts={duplicateEmailCheck.existingAccounts}
              email={formData.email}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Full Name
              </Typography>
              <Tooltip 
                title="Enter your full name"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              onBlur={validateName}
              placeholder="Enter your full name here"
              error={!!nameError}
              helperText={nameError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: nameError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: nameError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: nameError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Phone Number
              </Typography>
              <Tooltip 
                title="Enter your phone number"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="phonenumber"
              required
              value={formData.phonenumber}
              onChange={handleInputChange}
              onBlur={validatePhonenumber}
              placeholder="Enter your phone number here"
              error={!!phonenumberError}
              helperText={phonenumberError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: phonenumberError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: phonenumberError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: phonenumberError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
            />

            {passwordFields}

            {/* Registration Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <Button 
                variant="contained" 
                disabled={loading || duplicateEmailCheck.hasDuplicate} 
                onClick={handleRegister}
                sx={{ 
                  borderRadius: '0px', 
                  py: 1.2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: primaryColor, 
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(51, 51, 51, 0.3)',
                  '&:hover': { 
                    bgcolor: secondaryColor,
                    boxShadow: '0 12px 40px rgba(51, 51, 51, 0.4)',
                    transform: 'translateY(-1px)'
                  }, 
                  textTransform: 'none',
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'REGISTER'}
              </Button>
              
              <Button 
                variant="outlined" 
                onClick={() => navigate('/login')}
                sx={{ 
                  borderRadius: '0px', 
                  py: 1,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderColor: primaryColor, 
                  color: primaryColor, 
                  borderWidth: '2px',
                  '&:hover': { 
                    borderColor: secondaryColor, 
                    backgroundColor: 'rgba(51, 51, 51, 0.08)',
                    borderWidth: '2px'
                  },
                  textTransform: 'none'
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Calculator Tab at Bottom */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0,
        right: 0,
        zIndex: 1000,
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        pr: 2,
        gap: 1,
        flexWrap: 'wrap'
      }}>
        {/* Support Tab */}
        <Box
          onClick={() => setOpenSupport(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#D3D3D3',
            color: '#333',
            px: 4,
            py: 2,
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            minWidth: '300px',
            maxWidth: '400px',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#C8C8C8',
              transform: 'translateY(-2px)',
              boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.2)'
            },
            '@media (max-width: 1024px)': {
              minWidth: '260px',
              maxWidth: '340px',
              px: 3,
              py: 1.5,
              gap: 1.5
            },
            '@media (max-width: 768px)': {
              minWidth: '220px',
              maxWidth: '300px',
              px: 2,
              py: 1,
              gap: 1
            }
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '2px',
              flexShrink: 0,
              '@media (max-width: 1024px)': {
                width: 18,
                height: 18
              },
              '@media (max-width: 768px)': {
                width: 16,
                height: 16
              }
            }}
          >
            <EmailIcon sx={{ 
              fontSize: 14, 
              color: 'white',
              '@media (max-width: 1024px)': {
                fontSize: 12
              },
              '@media (max-width: 768px)': {
                fontSize: 10
              }
            }} />
          </Box>
          <Typography variant="body1" sx={{ 
            fontSize: '1rem', 
            fontWeight: 500,
            color: '#333',
            whiteSpace: 'nowrap',
            '@media (max-width: 1024px)': {
              fontSize: '0.9rem'
            },
            '@media (max-width: 768px)': {
              fontSize: '0.8rem'
            }
          }}>
            Contact Support
          </Typography>
        </Box>

        <Box
          onClick={() => setOpenCalculator(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#D3D3D3',
            color: '#333',
            px: 4,
            py: 2,
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            minWidth: '320px',
            maxWidth: '400px',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#C8C8C8',
              transform: 'translateY(-2px)',
              boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.2)'
            },
            '@media (max-width: 1024px)': {
              minWidth: '280px',
              maxWidth: '350px',
              px: 3,
              py: 1.5,
              gap: 1.5
            },
            '@media (max-width: 768px)': {
              minWidth: '240px',
              maxWidth: '300px',
              px: 2,
              py: 1,
              gap: 1
            }
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '2px',
              flexShrink: 0,
              '@media (max-width: 1024px)': {
                width: 18,
                height: 18
              },
              '@media (max-width: 768px)': {
                width: 16,
                height: 16
              }
            }}
          >
            <CalculatorIcon sx={{ 
              fontSize: 14, 
              color: 'white',
              '@media (max-width: 1024px)': {
                fontSize: 12
              },
              '@media (max-width: 768px)': {
                fontSize: 10
              }
            }} />
          </Box>
          <Typography variant="body1" sx={{ 
            fontSize: '1rem', 
            fontWeight: 500,
            color: '#333',
            whiteSpace: 'nowrap',
            '@media (max-width: 1024px)': {
              fontSize: '0.9rem'
            },
            '@media (max-width: 768px)': {
              fontSize: '0.8rem'
            }
          }}>
            Use our Bond Premium Calculator
          </Typography>
        </Box>
      </Box>

      <SupportContactDialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        context="Team Member Register"
      />

      <Dialog 
        open={openCalculator} 
        onClose={() => setOpenCalculator(false)} 
        sx={{ 
          '& .MuiPaper-root': { 
            borderRadius: '20px',
            maxWidth: '90vw'
          } 
        }}
      >
        <DialogContent>
          <PremiumCalculator />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenCalculator(false)}
            sx={{
              borderRadius: '12px',
              color: primaryColor,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(51, 51, 51, 0.08)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </Box>
  );
};

export default TeamMemberRegister;